import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assessmentsService, InterviewGuideResponse } from '../api/assessmentsService';
import AutoGrowTextarea from './AutoGrowTextarea';
import { AUTOSAVE_DELAY_MS, AUTOSAVE_RETRY_DELAY_MS, SaveState, saveStateLabel } from '../hooks/useDebouncedAutosave';

export const interviewGuideQueryKey = (assessmentId: number, assignmentId: number) => [
  'assessments',
  assessmentId,
  'report-assignments',
  assignmentId,
  'interview-guide',
];

export const useInterviewGuide = (assessmentId: number, assignmentId: number) =>
  useQuery({
    queryKey: interviewGuideQueryKey(assessmentId, assignmentId),
    queryFn: () => assessmentsService.getInterviewGuide(assessmentId, assignmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0 && Number.isFinite(assignmentId) && assignmentId > 0,
  });

const GENERAL_KEY = 'general';

export const InterviewGuideTab: React.FC<{
  assessmentId: number;
  assignmentId: number;
  canEdit: boolean;
  onContinueToReport?: () => void;
  continueLabel?: string;
}> = ({ assessmentId, assignmentId, canEdit, onContinueToReport, continueLabel = 'Continue to Report' }) => {
  const { data, isLoading, isError } = useInterviewGuide(assessmentId, assignmentId);
  const guideData = data?.data;
  const queryClient = useQueryClient();

  const [notes, setNotes] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const hydratedForRef = useRef<number | null>(null);
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  const notesRef = useRef<Record<string, string>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushRef = useRef<() => void>(() => {});

  notesRef.current = notes;

  useEffect(() => {
    if (!guideData || hydratedForRef.current === assignmentId) return;
    const initial: Record<string, string> = { [GENERAL_KEY]: guideData.generalNotes || '' };
    for (const section of guideData.sections) {
      for (const question of section.questions) {
        initial[String(question.id)] = question.notes || '';
      }
    }
    setNotes(initial);
    hydratedForRef.current = assignmentId;
  }, [guideData, assignmentId]);

  const saveMutation = useMutation({
    mutationFn: (payload: { notes?: Array<{ questionId: number; notes: string }>; generalNotes?: string }) =>
      assessmentsService.saveInterviewNotes(assessmentId, assignmentId, payload),
    onSuccess: () => {
      setSaveState(dirtyKeysRef.current.size ? 'pending' : 'saved');
      // Keeps the read-only notes summary on the Report tab in sync.
      queryClient.invalidateQueries({ queryKey: interviewGuideQueryKey(assessmentId, assignmentId) });
    },
    onError: () => setSaveState('error'),
  });

  const flush = useCallback(() => {
    const keys = Array.from(dirtyKeysRef.current);
    if (!keys.length) return;
    dirtyKeysRef.current = new Set();

    const payload: { notes?: Array<{ questionId: number; notes: string }>; generalNotes?: string } = {};
    const questionNotes = keys
      .filter((key) => key !== GENERAL_KEY)
      .map((key) => ({ questionId: parseInt(key, 10), notes: notesRef.current[key] ?? '' }));
    if (questionNotes.length) payload.notes = questionNotes;
    if (keys.includes(GENERAL_KEY)) payload.generalNotes = notesRef.current[GENERAL_KEY] ?? '';

    setSaveState('saving');
    saveMutation.mutate(payload, {
      onError: () => {
        // These fields were cleared from the dirty set before the request, so
        // without putting them back a failed save would silently drop the
        // assessor's last edit unless they happened to retype in that same
        // question. Restore them and retry on the slower timer.
        for (const key of keys) dirtyKeysRef.current.add(key);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { flushRef.current(); }, AUTOSAVE_RETRY_DELAY_MS);
      },
    });
  }, [saveMutation]);

  flushRef.current = flush;

  const handleChange = useCallback((key: string, value: string) => {
    setNotes((prev) => ({ ...prev, [key]: value }));
    dirtyKeysRef.current.add(key);
    setSaveState('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_DELAY_MS);
  }, [flush]);

  // Save anything still pending when the assessor navigates away mid-sentence.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasQuestions = useMemo(
    () => (guideData?.sections || []).some((section) => section.questions.length > 0),
    [guideData],
  );
  const editable = canEdit && Boolean(guideData?.canEdit);

  // Counted from live state, not the server copy, so it moves as they type.
  const progress = useMemo(() => {
    const allQuestions = (guideData?.sections || []).flatMap((section) => section.questions);
    const answered = allQuestions.filter((question) => (notes[String(question.id)] ?? '').trim()).length;
    return { answered, total: allQuestions.length };
  }, [guideData, notes]);

  if (isLoading) {
    return <div className="text-sm text-gray-700">Loading interview guide…</div>;
  }

  if (isError) {
    return <div className="text-sm text-red-700">Failed to load the interview guide.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {guideData?.guide ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900">{guideData.guide.title}</h2>
              {guideData.guide.subtitle && (
                <p className="text-sm text-gray-600">{guideData.guide.subtitle}</p>
              )}
            </>
          ) : (
            <h2 className="text-lg font-semibold text-gray-900">Interview Notes</h2>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Notes are optional, internal to the assessment team, and saved automatically as you type.
          </p>
        </div>
        <div className="text-right">
          {progress.total > 0 && (
            <div className="text-xs font-medium text-gray-700">
              {progress.answered} of {progress.total} questions have notes
            </div>
          )}
          <div
            className={[
              'text-xs',
              saveState === 'error' ? 'text-red-700' : 'text-gray-500',
            ].join(' ')}
          >
            {saveState === 'error' ? 'Could not save notes' : saveStateLabel[saveState]}
          </div>
        </div>
      </div>

      {guideData?.guide?.intro && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-gray-800">
          {guideData.guide.intro}
        </div>
      )}

      {!hasQuestions && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          No interview guide has been set up for this area yet. You can still keep general notes below.
        </div>
      )}

      {(guideData?.sections || []).map((section, sectionIndex) => (
        <div key={section.section || `section-${sectionIndex}`} className="space-y-4">
          {section.section && (
            <h3 className="text-sm font-semibold uppercase tracking-wide text-primary-700 border-b border-gray-200 pb-1">
              {section.section}
            </h3>
          )}
          {section.questions.map((question) => (
            <div key={question.id} className="space-y-1">
              <div className="text-sm font-medium text-gray-900">{question.prompt}</div>
              {question.helpText && <div className="text-xs text-gray-500">{question.helpText}</div>}
              <AutoGrowTextarea
                value={notes[String(question.id)] ?? ''}
                onChange={(value) => handleChange(String(question.id), value)}
                disabled={!editable}
                placeholder={editable ? 'Notes (optional)' : 'No notes'}
              />
            </div>
          ))}
        </div>
      ))}

      <div className="space-y-1 pt-2 border-t border-gray-200">
        <div className="text-sm font-medium text-gray-900">Comments / general notes</div>
        <AutoGrowTextarea
          value={notes[GENERAL_KEY] ?? ''}
          onChange={(value) => handleChange(GENERAL_KEY, value)}
          disabled={!editable}
          placeholder={editable ? 'Anything else worth remembering for the report' : 'No notes'}
          minHeight={120}
        />
      </div>

      {onContinueToReport && (
        <div className="flex justify-end pt-2">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            onClick={() => {
              flush();
              onContinueToReport();
            }}
          >
            {continueLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export interface InterviewNoteEntry {
  key: string;
  label: string;
  notes: string;
}

export const collectInterviewNotes = (guide?: InterviewGuideResponse): InterviewNoteEntry[] => {
  const rows: InterviewNoteEntry[] = [];
  for (const section of guide?.sections || []) {
    for (const question of section.questions) {
      if (question.notes?.trim()) {
        rows.push({ key: String(question.id), label: question.prompt, notes: question.notes });
      }
    }
  }
  if (guide?.generalNotes?.trim()) {
    rows.push({ key: GENERAL_KEY, label: 'Comments / general notes', notes: guide.generalNotes });
  }
  return rows;
};

const formatNoteForInsert = (entry: InterviewNoteEntry) => (
  entry.key === GENERAL_KEY ? entry.notes : `${entry.label}\n${entry.notes}`
);

/**
 * Read-only view of the assessor's interview notes shown beside the report
 * editor, so writing the report no longer means retyping from another tab.
 */
export const InterviewNotesPanel: React.FC<{
  guide?: InterviewGuideResponse;
  onInsert?: (text: string) => void;
}> = ({ guide, onInsert }) => {
  const entries = useMemo(() => collectInterviewNotes(guide), [guide]);

  if (!entries.length) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
        No interview notes yet. Anything you type on the Interview Guide tab shows up here.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-2">
        <div className="text-sm font-medium text-gray-900">Your interview notes ({entries.length})</div>
        {onInsert && (
          <button
            type="button"
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
            onClick={() => onInsert(entries.map(formatNoteForInsert).join('\n\n'))}
          >
            Insert all
          </button>
        )}
      </div>
      <div className="max-h-[32rem] overflow-y-auto px-4 py-3 space-y-4">
        {entries.map((entry) => (
          <div key={entry.key}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-medium text-gray-600">{entry.label}</div>
              {onInsert && (
                <button
                  type="button"
                  className="shrink-0 text-xs text-primary-600 hover:text-primary-700"
                  onClick={() => onInsert(formatNoteForInsert(entry))}
                >
                  Insert
                </button>
              )}
            </div>
            <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{entry.notes}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
